import { track } from "./umami";
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Download, FileText, MonitorDown, PackageOpen, ShieldCheck, Tag, X } from "lucide-react";
import { DOWNLOADS, downloadUrlFor, TERMS_VERSION, type DownloadPlatform } from "./config";
import {
  formatFileSize,
  formatReleaseDate,
  manifestDownloadFor,
  type ReleaseManifest
} from "./releaseManifest";

const ACCEPTANCE_STORAGE_KEY = "fileagent-terms-accepted-version";

/** Resolve the effective download URL for a platform from manifest → env → fallback. */
function resolveUrl(
  platform: DownloadPlatform,
  manifest: ReleaseManifest | null | undefined,
  publicDownloadUrl?: string
): string {
  const manifestUrl = manifestDownloadFor(manifest, platform)?.url;
  return downloadUrlFor(platform, { manifestUrl, publicDownloadUrl });
}

export function DownloadOptions({
  manifest,
  publicDownloadUrl,
  compact = false
}: {
  manifest?: ReleaseManifest | null;
  publicDownloadUrl?: string;
  compact?: boolean;
}) {
  const [selected, setSelected] = useState<DownloadPlatform>("linuxDeb");
  const [modalPlatform, setModalPlatform] = useState<DownloadPlatform | null>(null);

  const startRequest = (platform: DownloadPlatform) => {
    setSelected(platform);
    if (localStorage.getItem(ACCEPTANCE_STORAGE_KEY) === TERMS_VERSION) {
      startDownload(platform, manifest, publicDownloadUrl);
      return;
    }
    track("download_terms_shown", { platform, terms_version: TERMS_VERSION });
    setModalPlatform(platform);
  };

  const version = manifest?.latest_version;
  const releaseDate = formatReleaseDate(manifest?.release_date);
  const selectedDownload = manifestDownloadFor(manifest, selected);

  return (
    <div className={compact ? "download-options compact-downloads" : "download-options"}>
      {version && (
        <p className="download-version-line">
          <Tag size={14} aria-hidden="true" />
          <span>
            Latest version <strong>{version}</strong>
            {releaseDate ? ` · Released ${releaseDate}` : ""}
          </span>
        </p>
      )}
      <div className="platform-choice" role="radiogroup" aria-label="Choose your FileAgent download platform">
        {(Object.keys(DOWNLOADS) as DownloadPlatform[]).map((platform) => {
          const item = DOWNLOADS[platform];
          const active = selected === platform;
          return (
            <button
              key={platform}
              type="button"
              className={`platform-card ${active ? "selected" : ""}`}
              role="radio"
              aria-checked={active}
              onClick={() => { setSelected(platform); track("download_selected", { platform }); }}
            >
              <span className="platform-icon" aria-hidden="true">
                {platform === "windows" ? <MonitorDown size={21} /> : <PackageOpen size={21} />}
              </span>
              <span>
                <strong>{item.label}</strong>
                <small>{item.fileType} {item.extension}</small>
              </span>
            </button>
          );
        })}
      </div>
      <div className="download-actions">
        <button className="button button-primary" type="button" onClick={() => startRequest(selected)}>
          <Download size={18} /> Download {DOWNLOADS[selected].platformLabel}
        </button>
      </div>
      {selectedDownload && (selectedDownload.sha256 || selectedDownload.size_bytes > 0) && (
        <details className="download-verify">
          <summary>
            <ShieldCheck size={14} aria-hidden="true" /> Verify your download (SHA-256)
          </summary>
          <div className="download-verify-body">
            <p>
              {selectedDownload.filename}
              {selectedDownload.size_bytes > 0 ? ` · ${formatFileSize(selectedDownload.size_bytes)}` : ""}
            </p>
            {selectedDownload.sha256 && (
              <code className="download-hash">{selectedDownload.sha256}</code>
            )}
            <p className="download-verify-help">
              Confirm the SHA-256 of the file you downloaded matches the value above before installing.
            </p>
          </div>
        </details>
      )}
      {modalPlatform && (
        <TermsAcceptanceModal
          platform={modalPlatform}
          manifest={manifest}
          publicDownloadUrl={publicDownloadUrl}
          onCancel={() => { track("download_terms_cancelled", { platform: modalPlatform }); setModalPlatform(null); }}
          onAccepted={() => {
            track("download_terms_accepted", { platform: modalPlatform, terms_version: TERMS_VERSION });
            localStorage.setItem(ACCEPTANCE_STORAGE_KEY, TERMS_VERSION);
            startDownload(modalPlatform, manifest, publicDownloadUrl);
            setModalPlatform(null);
          }}
        />
      )}
    </div>
  );
}

function TermsAcceptanceModal({
  platform,
  manifest,
  publicDownloadUrl,
  onCancel,
  onAccepted
}: {
  platform: DownloadPlatform;
  manifest?: ReleaseManifest | null;
  publicDownloadUrl?: string;
  onCancel: () => void;
  onAccepted: () => void;
}) {
  const [accepted, setAccepted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLInputElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const download = DOWNLOADS[platform];
  const url = useMemo(() => resolveUrl(platform, manifest, publicDownloadUrl), [platform, manifest, publicDownloadUrl]);

  useEffect(() => {
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    firstFocusRef.current?.focus();
    document.body.classList.add("modal-open");

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("modal-open");
      previousFocus.current?.focus();
    };
  }, [onCancel]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onCancel();
    }}>
      <div
        className="terms-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-title"
        aria-describedby="terms-modal-description"
        ref={dialogRef}
      >
        <div className="modal-header">
          <span className="icon-badge"><ShieldCheck size={18} /></span>
          <button className="icon-button modal-close" type="button" onClick={onCancel} aria-label="Cancel download">
            <X size={18} />
          </button>
        </div>
        <h2 id="terms-modal-title">Review terms before downloading FileAgent</h2>
        <p id="terms-modal-description">
          You selected <strong>{download.platformLabel}</strong> ({download.fileType} {download.extension}). By downloading and using FileAgent, you agree to the Terms of Service and acknowledge the Privacy Policy.
        </p>
        <div className="modal-link-row">
          <a href="/terms" target="_blank" rel="noopener">
            <FileText size={16} aria-hidden="true" />
            <span>Terms of Service</span>
          </a>
          <a href="/privacy" target="_blank" rel="noopener">
            <ShieldCheck size={16} aria-hidden="true" />
            <span>Privacy Policy</span>
          </a>
        </div>
        <label className="terms-check">
          <input ref={firstFocusRef} type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
          <span>I have read and agree to the Terms of Service.</span>
        </label>
        <div className="modal-actions">
          <button className="button button-secondary" type="button" onClick={onCancel}>Cancel</button>
          <button className="button button-primary" type="button" disabled={!accepted} onClick={onAccepted}>
            <Check size={18} /> Accept and Download
          </button>
        </div>
        <p className="download-url-note">Download target: <code>{url}</code></p>
      </div>
    </div>
  );
}

function startDownload(platform: DownloadPlatform, manifest?: ReleaseManifest | null, publicDownloadUrl?: string) {
  const url = resolveUrl(platform, manifest, publicDownloadUrl);
  track("download_requested", { platform, version: manifest?.latest_version });
  const anchor = document.createElement("a");
  anchor.setAttribute("data-fileagent-skip", "");
  anchor.href = url;
  anchor.rel = "noopener";
  anchor.download = "";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}
